/**
 * Site location — pure URL parsing + map-launch URL builders (Step-2 Slice 2B).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  OWNER-LOCKED FEATURE (memory: project_avbp_site_location_feature, 2026-06-07).
 *  The Vastu engine does NOT use property coordinates — this is a pure
 *  AV-workflow convenience: open a project → one click → see the site on a
 *  map + the stored north bearing, in one glance.
 *
 *  This module is the ENTIRE parse/format brain and is unit-tested in
 *  isolation. It carries NO Firebase / React import so the Node test runner
 *  loads it via `_tsload.cjs` with zero side effects. The impure Firestore
 *  write of `answers.site_location` lives in `staffIntake.ts`.
 *
 *  Stored shape (additive — never collides with any composer-read key;
 *  verified no `answers.site_location` read exists in the composer):
 *    answers.site_location = {
 *      lat: number | null,
 *      lon: number | null,
 *      google_maps_url: string,
 *      source: 'manual' | 'google_link'
 *    }
 * ─────────────────────────────────────────────────────────────────────────
 */

/** A parsed site location (the form/doc shape). */
export interface SiteLocation {
  lat: number | null;
  lon: number | null;
  google_maps_url: string;
  source: 'manual' | 'google_link';
}

/** Result of parsing a pasted Google Maps URL. */
export interface ParsedCoords {
  lat: number;
  lon: number;
  /** Which extraction strategy hit — useful for tests + UI hinting. */
  via: 'pin' | 'at' | 'query';
}

/** Latitude is valid in [-90, 90]; longitude in [-180, 180]. */
export function isValidLat(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= -90 && n <= 90;
}
export function isValidLon(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= -180 && n <= 180;
}

/** A coordinate pair is plottable only when BOTH halves are in range. */
export function hasValidCoords(loc: {
  lat: number | null;
  lon: number | null;
}): loc is { lat: number; lon: number } {
  return isValidLat(loc.lat) && isValidLon(loc.lon);
}

/**
 * Coerce a free-typed lat/lon string to a finite number in range, or null.
 * Never returns NaN or an out-of-range value — a half-typed coordinate must
 * read as "absent", not 0.
 */
export function coordOrNull(v: unknown, kind: 'lat' | 'lon'): number | null {
  if (typeof v === 'number') {
    const ok = kind === 'lat' ? isValidLat(v) : isValidLon(v);
    return ok ? v : null;
  }
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const ok = kind === 'lat' ? isValidLat(n) : isValidLon(n);
  return ok ? n : null;
}

/**
 * Is this a Google short link that CANNOT be expanded client-side (CORS
 * blocks the redirect read)? The UI shows a "paste the full link" hint for
 * these; manual lat/lon is always the reliable fallback.
 */
export function isShortGoogleLink(url: string): boolean {
  const s = (url || '').trim();
  return /maps\.app\.goo\.gl\//i.test(s) || /goo\.gl\/maps\//i.test(s);
}

/**
 * PURE — extract {lat, lon} from a FULL Google Maps URL, client-side, free
 * (no Places API). Strategy priority (most-precise first):
 *   1. pin coords     `!3d<lat>!4d<lon>`     (the dropped pin — most exact)
 *   2. view centre    `@<lat>,<lon>[,zoom]`  (the map centre)
 *   3. query forms    `?q=` / `query=` / `ll=` / `daddr=` = `<lat>,<lon>`
 * Returns null when no strategy matches (e.g. a short link, a place-name
 * search with no coords, or junk) — the caller then shows the hint and
 * leaves manual entry as the path.
 */
export function parseGoogleMapsUrl(url: string): ParsedCoords | null {
  const s = (url || '').trim();
  if (!s) return null;

  // 1 — pin coords: !3d<lat>!4d<lon>
  const pin = s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (pin) {
    const lat = Number(pin[1]);
    const lon = Number(pin[2]);
    if (isValidLat(lat) && isValidLon(lon)) return { lat, lon, via: 'pin' };
  }

  // 2 — view centre: @<lat>,<lon>
  const at = s.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) {
    const lat = Number(at[1]);
    const lon = Number(at[2]);
    if (isValidLat(lat) && isValidLon(lon)) return { lat, lon, via: 'at' };
  }

  // 3 — query forms: q= / query= / ll= / daddr= = "<lat>,<lon>"
  //     (also tolerates an encoded comma %2C and surrounding whitespace)
  const decoded = safeDecode(s);
  const q = decoded.match(
    /(?:[?&](?:q|query|ll|daddr|destination|center)=)\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/i,
  );
  if (q) {
    const lat = Number(q[1]);
    const lon = Number(q[2]);
    if (isValidLat(lat) && isValidLon(lon)) return { lat, lon, via: 'query' };
  }

  return null;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/* ─────────────────────────── map-launch URLs ──────────────────────────── */

/** Google Maps search pin at the coordinates. */
export function googleMapsUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
}

/** Google Earth web — search the coordinates so a pin is dropped at the exact
 *  spot (the `@…` camera form only re-centres the view without marking the
 *  plot, which made it hard to see which location was meant). */
export function googleEarthUrl(lat: number, lon: number): string {
  return `https://earth.google.com/web/search/${lat},${lon}`;
}

/** Apple Maps drop-pin at the coordinates. */
export function appleMapsUrl(lat: number, lon: number): string {
  return `https://maps.apple.com/?ll=${lat},${lon}&q=Site`;
}

/** All three launch URLs at once (the project-view quick buttons). */
export function mapLaunchUrls(
  lat: number,
  lon: number,
): { google: string; earth: string; apple: string } {
  return {
    google: googleMapsUrl(lat, lon),
    earth: googleEarthUrl(lat, lon),
    apple: appleMapsUrl(lat, lon),
  };
}

/* ─────────────── combined coordinate field (one "lat, long" box) ──────────
 *  Step-2 Slice 2C (2026-06-08). The staff panel now has ONE coordinate field
 *  instead of two. The owner pastes "lat,long" copied straight from Google and
 *  also copies this value back OUT to paste into Google/Earth, so the canonical
 *  display string must round-trip cleanly with Google: `lat, long` decimal.
 *
 *  Input is tolerant: decimal pairs (with/without spaces, with a minus for
 *  S/W) AND degree-minute-second (DMS) with N/S/E/W hemisphere suffixes,
 *  comma- OR space-separated. Everything parses down to a canonical decimal
 *  {lat, lon}; `formatCoordPair` renders the round-trip "lat, long" string.
 *  Pure — no Firebase/React; unit-tested in isolation. */

/** A parsed combined-field coordinate pair (canonical decimal). */
export interface CoordPair {
  lat: number;
  lon: number;
}

/** Round a decimal degree to a sensible fixed precision (~1 cm) WITHOUT
 *  trailing-zero noise, so the canonical string round-trips with Google. */
function trimDeg(n: number): number {
  // 7 dp ≈ 1.1 cm at the equator — finer than any site needs, no float fuzz.
  return Math.round(n * 1e7) / 1e7;
}

/**
 * PURE — parse ONE DMS token like `22°43'10.6"N`, `75 51 27.7 E`,
 * `22 43 10.6` (no suffix), `-22°43'10.6"` to a signed decimal degree, or
 * null if it is not DMS-shaped. Accepts °/'/" or spaces as separators; a
 * trailing N/S/E/W (or a leading minus) sets the sign. Minutes/seconds are
 * optional (degrees-only `22 N` is allowed). Range-checking is the caller's.
 */
export function parseDmsToken(token: string): number | null {
  const t = (token || '').trim();
  if (!t) return null;
  // hemisphere suffix (or prefix) — case-insensitive
  const hemi = t.match(/([NSEW])\s*$/i) || t.match(/^\s*([NSEW])/i);
  const hemiCh = hemi ? hemi[1].toUpperCase() : '';
  // strip the hemisphere letter, keep the rest
  const body = t.replace(/[NSEW]/gi, '').trim();
  // a leading minus on the numeric body
  const neg = /^-/.test(body);
  // pull up to three numbers: degrees [minutes [seconds]]
  const nums = body.match(/\d+(?:\.\d+)?/g);
  if (!nums || nums.length === 0) return null;
  // DMS requires SOME structure: either a hemisphere letter, or a °/'/" mark,
  // or more than one number. A bare single number with no marker is NOT DMS —
  // that path belongs to the decimal parser (so "22.7196" isn't misread).
  const hasDmsMark = /[°'"′″]/.test(t);
  if (!hemiCh && !hasDmsMark && nums.length < 2) return null;
  const deg = Number(nums[0]);
  const min = nums.length > 1 ? Number(nums[1]) : 0;
  const sec = nums.length > 2 ? Number(nums[2]) : 0;
  if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec))
    return null;
  if (min >= 60 || sec >= 60) return null; // malformed DMS
  let val = deg + min / 60 + sec / 3600;
  if (neg || hemiCh === 'S' || hemiCh === 'W') val = -val;
  return trimDeg(val);
}

/**
 * PURE — parse the COMBINED coordinate field into a canonical decimal
 * {lat, lon}, or null when it can't be understood / is out of range. Accepts:
 *   · decimal pair: "22.7196, 75.8577" · "-22.7196,-75.8577" · "22.7196 75.8577"
 *   · DMS pair: `22°43'10.6"N 75°51'27.7"E` · `22 43 10.6 N, 75 51 27.7 E`
 * The two halves may be separated by a comma OR (for decimal/space-DMS) the
 * gap is inferred. Latitude must be the FIRST half (Google's "lat,long" order).
 */
export function parseCoordPair(raw: string): CoordPair | null {
  const s = (raw || '').trim();
  if (!s) return null;

  // Split into the two halves. A comma is the unambiguous separator; if there
  // is no comma, split DMS pairs on the hemisphere boundary, and decimal pairs
  // on whitespace.
  let latStr = '';
  let lonStr = '';
  if (s.includes(',')) {
    const parts = s.split(',');
    if (parts.length !== 2) return null;
    latStr = parts[0].trim();
    lonStr = parts[1].trim();
  } else if (/[NSEW]/i.test(s)) {
    // DMS pair, space-separated: split AFTER the first hemisphere letter.
    const m = s.match(/^(.*?[NSEW])\s+(.*)$/i);
    if (!m) return null;
    latStr = m[1].trim();
    lonStr = m[2].trim();
  } else {
    // decimal pair separated by whitespace: "22.7196 75.8577"
    const parts = s.split(/\s+/);
    if (parts.length !== 2) return null;
    latStr = parts[0].trim();
    lonStr = parts[1].trim();
  }
  if (!latStr || !lonStr) return null;

  const lat = parseOneCoord(latStr, 'lat');
  const lon = parseOneCoord(lonStr, 'lon');
  if (lat == null || lon == null) return null;
  return { lat, lon };
}

/** Parse a single half — DMS first (if it looks like DMS), else plain decimal —
 *  range-checked for its axis. */
function parseOneCoord(token: string, kind: 'lat' | 'lon'): number | null {
  const t = token.trim();
  // DMS shape: a hemisphere letter or a °/'/" mark or 2+ numbers.
  const looksDms = /[NSEW°'"′″]/i.test(t) || /\d+(?:\.\d+)?\s+\d/.test(t);
  let v: number | null = null;
  if (looksDms) {
    v = parseDmsToken(t);
  } else {
    const n = Number(t);
    v = Number.isFinite(n) ? trimDeg(n) : null;
  }
  if (v == null) return null;
  const ok = kind === 'lat' ? isValidLat(v) : isValidLon(v);
  return ok ? v : null;
}

/** PURE — the canonical round-trip "lat, long" decimal string for the combined
 *  field (and for copy-out into Google/Earth). Empty string when either half
 *  is absent. */
export function formatCoordPair(
  lat: number | null,
  lon: number | null,
): string {
  if (!isValidLat(lat) || !isValidLon(lon)) return '';
  return `${trimDeg(lat as number)}, ${trimDeg(lon as number)}`;
}

/* ───────────────────── build the stored doc shape ─────────────────────── */

/**
 * PURE — build the `answers.site_location` doc value from the form's two
 * inputs (manual lat/lon + the pasted url). If the url parses, its coords win
 * and `source='google_link'`; otherwise the manual lat/lon are used and
 * `source='manual'`. Out-of-range or blank coords are written as `null` (so a
 * half-filled field never persists a junk number). Always returns a complete,
 * stable shape so the merge write is predictable.
 */
export function buildSiteLocation(input: {
  /** The single combined "lat, long" field (decimal OR DMS). Preferred. */
  coordPair?: string;
  /** Legacy split inputs — still honoured if `coordPair` is absent. */
  manualLat?: string | number | null;
  manualLon?: string | number | null;
  googleUrl: string;
}): SiteLocation {
  const url = (input.googleUrl || '').trim();
  const parsed = url ? parseGoogleMapsUrl(url) : null;

  // SINGLE SOURCE OF TRUTH (16th dispatch): the combined coordinate FIELD is now
  // always authoritative for the stored lat/lon. Pasting a Google link extracts
  // its coords INTO the field (the panel does this on paste), so by the time we
  // build, `coordPair` already carries the effective value — whether it came
  // from a link or was typed/edited by the owner. We still keep the url and label
  // `source='google_link'` when a link is present (provenance), but the NUMBER
  // comes from the field, so a manual edit after a paste wins.
  if (input.coordPair != null && input.coordPair.trim() !== '') {
    const pair = parseCoordPair(input.coordPair);
    return {
      lat: pair ? pair.lat : null,
      lon: pair ? pair.lon : null,
      google_maps_url: url,
      source: parsed ? 'google_link' : 'manual',
    };
  }
  // No field value: fall back to the parsed link (e.g. a link pasted before the
  // panel mirrored it into the field), else the legacy split inputs.
  if (parsed) {
    return {
      lat: parsed.lat,
      lon: parsed.lon,
      google_maps_url: url,
      source: 'google_link',
    };
  }
  return {
    lat: coordOrNull(input.manualLat, 'lat'),
    lon: coordOrNull(input.manualLon, 'lon'),
    google_maps_url: url,
    source: 'manual',
  };
}

/**
 * PURE — hydrate the form fields from a stored `answers.site_location` (for
 * resume/edit). Defensive across a partial/absent doc.
 */
export function siteLocationFromDoc(
  v: Record<string, unknown> | null | undefined,
): SiteLocation {
  const o = v || {};
  const lat = coordOrNull(o.lat as unknown, 'lat');
  const lon = coordOrNull(o.lon as unknown, 'lon');
  const url = typeof o.google_maps_url === 'string' ? o.google_maps_url : '';
  const src = o.source === 'google_link' ? 'google_link' : 'manual';
  return { lat, lon, google_maps_url: url, source: src };
}
